'use client';

import './pwa-banner.scss';

import React, { useEffect, useState } from 'react';

import Close from '@/assets/icons/close-normal.svg';

import {
    getLocalStorageItem,
    isClient,
    setLocalStorageItem,
} from '../../helpers/utils';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';

interface PWAInfoBannerProps {
    dictionary?: ILibraryMeetingPlaceDictionary & { cloneInfoBanner?: string };
    meetingPlaceName: string;
    /** Official domain shown in bold. Falls back to NEXT_PUBLIC_OFFICIAL_URL. */
    officialUrl: string;
}

/**
 * Info-only banner for a single meeting place, toggled via the
 * NEXT_PUBLIC_PWA_INFO_BANNER env flag. It reuses the PWA banner design but only
 * shows an informational message — no install or close actions — and is not
 * dismissible. The text comes from the `pwaInfoBanner` dictionary field.
 */
const PWAInfoBanner: React.FC<PWAInfoBannerProps> = ({
    dictionary,
    meetingPlaceName,
    officialUrl,
}) => {
    const [isClosed, setIsClosed] = useState<boolean>(false);

    // Toggle the shared `pwaBanner-show` class on <html> so this banner reuses
    // the exact same layout treatment as the PWA banner: it renders on top and
    // pushes the page content down (via `smartbanner-top-margin`) instead of
    // overlapping it, and shifts the fixed nav buttons down.
    const showBanner = () => {
        if (isClient) {
            window.document.querySelector('html')?.classList.add('pwaBanner-show');
        }
    };

    const hideBanner = () => {
        if (isClient) {
            window.document
                .querySelector('html')
                ?.classList.remove('pwaBanner-show');
        }
    };

    // Restore the dismissed state so the banner stays closed on return visits,
    // mirroring the PWA banner behavior.
    useEffect(() => {
        if (
            isClient &&
            getLocalStorageItem('cloneInfoBanner-closed') === 'true'
        ) {
            setIsClosed(true);
            return;
        }

        showBanner();

        // Ensure the layout offset is cleared if the banner unmounts.
        return () => {
            hideBanner();
        };
    }, []);

    const handleClose = () => {
        setLocalStorageItem('cloneInfoBanner-closed', 'true');
        setIsClosed(true);
        hideBanner();
    };

    // Message format (from the `cloneInfoBanner` dictionary field):
    //   "{url} is the only official website of {Meeting Place}. Please verify the
    //    domain before continuing."
    // The `{url}` and `{Meeting Place}` tokens are rendered in bold and can each
    // carry a different value.
    const url = officialUrl;
    const template = dictionary?.cloneInfoBanner ?? '';
    const values: Record<string, string> = {
        '{url}': url,
        '{Meeting Place}': meetingPlaceName,
    };
    const parts = template.split(/(\{url\}|\{Meeting Place\})/g);

    if (isClosed) {
        return null;
    }

    return (
        <div className="pwaBanner bg-background-primary-light py-2 px-4">
            <div className="relative flex h-15 items-center justify-center">
                <button
                    type="button"
                    aria-label="close"
                    onClick={handleClose}
                    className="absolute left-0"
                >
                    <Close
                        className="h-2 w-2 cursor-pointer from-neutral-grey-400"
                        viewBox="0 0 14 14"
                    />
                </button>
                <div className="flex flex-col justify-center px-8">
                    <div className="text-bodys sm:text-bodyl text-neutral-grey-400 text-center">
                        {parts.map((part, index) =>
                            values[part] !== undefined ? (
                                <React.Fragment key={index}>
                                    <strong className="font-bold">
                                        {values[part]}
                                    </strong>{' '}
                                </React.Fragment>
                            ) : (
                                <React.Fragment key={index}>{part}</React.Fragment>
                            ),
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PWAInfoBanner;
